import { useMemo } from 'react';
import { Box, Typography, Card, CardContent, Grid } from '@mui/material';
import Chart from 'react-apexcharts';

const METRICS = [
    { key: 'funding', label: 'Funding', prefix: true },
    { key: 'time_to_market_months', label: 'Time to Market', suffix: ' months' },
    { key: 'profitability_months', label: 'Profitability', suffix: ' months' },
    { key: 'burn_rate', label: 'Monthly Burn Rate', prefix: true },
];

export default function GatewayImpactSimulator({ deal }) {
    const hasData = deal?.without_gateway_funding || deal?.with_gateway_funding;

    const chartData = useMemo(() => {
        if (!hasData) return null;

        return METRICS.map((metric) => {
            const withoutKey = `without_gateway_${metric.key}`;
            const withKey = `with_gateway_${metric.key}`;
            const without = Number(deal[withoutKey] || 0);
            const withGw = Number(deal[withKey] || 0);

            return {
                ...metric,
                without,
                withGw,
                improvement: without > 0
                    ? (((without - withGw) / without) * 100).toFixed(1)
                    : '0.0',
            };
        });
    }, [deal, hasData]);

    if (!hasData) {
        return (
            <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="h6" color="text.secondary">Impact Simulator</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        No impact data available for this deal yet.
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const barOptions = {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
        plotOptions: {
            bar: { horizontal: false, columnWidth: '55%', borderRadius: 6 },
        },
        dataLabels: { enabled: false },
        xaxis: {
            categories: chartData.map(m => m.label),
        },
        yaxis: { labels: { formatter: (v) => Number(v).toLocaleString() } },
        colors: ['#e53935', '#1a237e'],
        legend: { position: 'top' },
        tooltip: {
            y: { formatter: (v) => Number(v).toLocaleString() },
        },
    };

    const barSeries = [
        { name: 'Without Gateway', data: chartData.map(m => m.without) },
        { name: 'With Gateway', data: chartData.map(m => m.withGw) },
    ];

    return (
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Gateway Impact Simulator
            </Typography>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
                {chartData.map((metric) => (
                    <Grid item xs={6} sm={3} key={metric.key}>
                        <Card variant="outlined">
                            <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    {metric.label}
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 1 }}>
                                    <Box>
                                        <Typography variant="caption" color="error.main">Without</Typography>
                                        <Typography variant="body2" fontWeight={700} color="error.main">
                                            {metric.prefix && deal.currency_label ? `${deal.currency_label} ` : ''}
                                            {metric.without.toLocaleString()}
                                            {metric.suffix || ''}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="primary.main">With</Typography>
                                        <Typography variant="body2" fontWeight={700} color="primary.main">
                                            {metric.prefix && deal.currency_label ? `${deal.currency_label} ` : ''}
                                            {metric.withGw.toLocaleString()}
                                            {metric.suffix || ''}
                                        </Typography>
                                    </Box>
                                </Box>
                                {Number(metric.improvement) > 0 && (
                                    <Typography variant="caption" sx={{
                                        color: 'success.main', fontWeight: 700,
                                        bgcolor: 'success.lighter', px: 1, py: 0.25, borderRadius: 1,
                                    }}>
                                        {metric.improvement}% improvement
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Bar Chart */}
            <Card>
                <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                        Before vs After Gateway Comparison
                    </Typography>
                    <Chart options={barOptions} series={barSeries} type="bar" height={360} />
                </CardContent>
            </Card>
        </Box>
    );
}
